/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faLink } from '@fortawesome/free-solid-svg-icons';
import copy from 'copy-to-clipboard';
import Modal from 'app/components/commons/Modal';
import Button from 'app/components/commons/Button';
import withNotificationCallback from 'app/components/notifications/withNotificationCallback';
import t from 'app/utils/translate';
import { COPY_COLOR } from 'app/constants/socialColorTypes';
import SocialButton from '../../components/commons/SocialButton';

type Props = {
  isOpen: boolean,
  onCancel: () => void,
  links: Array<string>,
  transmitNotification: Object => void
};

const Wrapper = styled.div(
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  ({ theme }) => ({
    fontSize: theme.fontSize.small,
    paddingTop: theme.spacing.default
  })
);

const Body = styled.div(({ theme, paddingTop = true, paddingBottom = true }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  width: '100%',
  paddingTop: paddingTop ? theme.spacing.default : 'auto',
  paddingBottom: paddingBottom ? theme.spacing.default : 'auto'
}));

const Bold = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeight.demiBold,
  fontSize: theme.fontSize.medium
}));

const ButtonContainer = styled.div(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  '& > * ': {
    marginRight: theme.spacing.small
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  width: '150px',
  margin: theme.spacing.xsmall,
  marginBottom: theme.spacing.large,
  textAlign: 'center',
  [theme.mediaQuery.mobileSmallOnly]: {
    width: '120px'
  }
}));

const ShareLink = styled.div(({ theme }) => ({
  marginBottom: theme.spacing.small
}));

const SuccessIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  color: theme.color.success
}));

const CopyButton = styled(SocialButton)(({ theme }) => ({
  fontSize: theme.fontSize.default,
  width: 250,
  marginBottom: theme.spacing.tiny
}));

const ButtonLabel = styled.span({
  textTransform: 'uppercase'
});

const contest = t('challenge').toLowerCase();

const SuccessModal: React.FC = ({ isOpen, onCancel, links, transmitNotification }: Props) => {
  const handleCopy = link => {
    copy(link);
    transmitNotification({
      message: t('Url copied to clipboard'),
      type: 'success'
    });
  };

  const handleUnmount = () => onCancel;

  React.useEffect(handleUnmount, []);

  return (
    <Modal isOpen={isOpen} onCancel={onCancel} shouldCloseOnOverlayClick size="medium">
      <Wrapper>
        <SuccessIcon icon={faCheckCircle} size="4x" />
        <Body>
          <Bold>Your team has been successfully submitted!</Bold>
          <Bold>Follow your score on the Live Leaderboard once the {contest} starts!</Bold>
        </Body>
        {Array.isArray(links) && links.length > 0 && (
          <React.Fragment>
            <hr color="lightgrey" width="100%" />
            <Body paddingBottom={false}>
              <Bold>Share the {contest} with your friends</Bold>
            </Body>
            <Body>
              {links.map(link => (
                <ShareLink>
                  <CopyButton backgroundColor={COPY_COLOR} onClick={() => handleCopy(link)}>
                    <ButtonLabel>COPY {contest} LINK&nbsp;&nbsp;</ButtonLabel>
                    <FontAwesomeIcon icon={faLink} />
                  </CopyButton>
                  <div>{link}</div>
                </ShareLink>
              ))}
            </Body>
          </React.Fragment>
        )}
        <ButtonContainer>
          <StyledButton size="medium" kind="secondary" link="/lobby">
            {t('Lobby')}
          </StyledButton>
          <StyledButton size="medium" kind="primary" link="/my-teams">
            {t('My Teams')}
          </StyledButton>
          <StyledButton size="medium" kind="primary" onClick={onCancel}>
            {t('Create More')}
          </StyledButton>
        </ButtonContainer>
      </Wrapper>
    </Modal>
  );
};

export default withNotificationCallback(SuccessModal);
